import * as React from "react";
import { I18n } from "react-redux-i18n";

import { useController } from "./controllers";
import { MessageBox } from "@common/components/MessageBox";
import { InvitesTable } from "./components/invites-table";

import {
  getReviewedStatusState,
  generateLetterFromInvites,
  saveAdditionalDatasheetFormatData,
  getAdditionalDatasheetFormatData,
  createPedigree,
} from "@common/actions/invites";
import {
  getFamilyIdMembersCount,
  getIndividualEmail,
  getIndividualFamilyData,
  getSentQuestionnaires,
  openDatasheetFromIndividual,
  openPedigreeFromIndividual,
  saveIndividualEmail,
  getInviteHistory,
  getIndividuals,
} from "@common/actions/individuals";
import {
  getQuestionnaireForInvite,
  getQuestionnaires,
  openInviteQuestionnaire,
} from "@common/actions/questionnaires";
import {
  checkPedigreeNameExists,
  createPedigreeAndMoveToFolder,
  getPedigreeFolders,
  getPedigreeMembersCount,
} from "@common/actions/pedigrees";
import { getFolderFields } from "@common/actions/formats";
import { validateIndividualEmail } from "@modules/individuals/actions/individuals";
import { createBatchLetters } from "@common/actions/letters";
import { Header } from "@common/components/Header";
import {
  getFolders,
  getModuleFolders,
  getInviteQueryFolder,
  getFormateFromInviteFolder,
} from "@common/actions/folders";
import { Flex } from "@common/components/Flex";
import { Search } from "@common/components/Search";
import { Button } from "@common/components/Button";
import { getTestAttributes } from "@common/utils/tests";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { getModuleFolderFields } from "@common/actions/formats";
import {
  getPedigreesColumns,
  savePedigreesColumns,
} from "@modules/pedigrees/services/pedigrees";
import { getAdminSmartListFunctions } from "@common/actions/permissions";
import {
  QueryManagement,
  QueryManagementFormat,
  QueryManagementType,
} from "@common/components/query-management";
import {
  getInviteMetadata,
  getFhqInviteRelatives,
  getFhqInviteIndividuals,
} from "@modules/invite-status/services/invites";
import { InvitesExport } from "./components/invites-export";
import { InvitesFilter } from "./components/invites-filter";
import { PatientInvite } from "./components/patient-invite";
import { MODULE_NAME } from "./constants/shared";
import { getPedigrees } from "./services/pedigrees";
import { getIndividualsColumns, getInvitesFilter } from "./services/invites";
import { QueryCreating } from "./components/query-creating";
import { getAppConfig } from "@common/utils/config";

import random from "lodash/random"; //TODO: for mocking folders and formats
import range from "lodash/range"; //TODO: for mocking folders and formats
import ActionsButton from "@common/components/ActionsButton";
import { FolderContext } from "@common/dto/folder/context";
import { updateEntity } from "@common/actions/data";

import {
  getQueryFormatFolders,
  deleteQueryFormatFolder,
  saveQueryFormatFolder,
} from "./services/folders";
import {
  getQueryFormat,
  saveQueryFormats,
  deleteQueryFormat,
  searchQueryFormats,
  getQueryFormats,
} from "./services/formats";

import "./invite-status.styles.scss";

export type InviteStatusProps = {};

export const InviteStatus: React.FC<InviteStatusProps> = () => {
  const {
    INVITES_LOAD_ITEMS_COUNT,
    openPatientInvite,
    patientInviteOpened,
    closePatientInvite,
    handlePatientInviteSubmit,
    handleInviteSelectedItemsChange,
    handleLaunchNewQuestionnaire,
    getQuestionnairesForNewQuestionnaire,
    adminFunctions,
    filters,
    getFoldersForInvitesColumn,
    showMessage,
    inviteItemUniqProperty,
    invitesLoading,
    invitesSelectedItems,
    invitesLoadMetadata,
    invitesCount,
    invitesColumns,
    invites,
    handleNewInvitesSend,
    handleRestoreInvitesColumnsToDefault,
    updateInvitesColumns,
    handleInvitesColumnsOrderChange,
    handleGetInvites,
    getFieldsForInvitesColumn,
    messageProps,
    getColumnId,
    showErrorMessage,
    closeMessage,
    showInfoMessage,
    showConfirmMessage,
    handleUpdateInvitesLockStatus,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    handleGetFilteredInvites,
    inviteStatusFilter,
    invitesExportDialogOpened,
    closeInvitesExportDialog,
    openInvitesExportDialog,
    handleSubmitInvitesExport,
    closeLoadQueryManagement,
    loadQueryManagementType,
    handleQueryManagementSubmit,
    closeQueryCreatingDialog,
    queryCreatingDialogOpened,
    handleInvitesSearch,
    invitesSearchKeyword,
    queryFormatsActions,
    clearQueryBtnDisabled,
    allInvites,
    handleClearQueryFormatFilter
  } = useController();

  const invitesStatusConfig = getAppConfig().inviteStatus || {};

  const individualsContext = FolderContext.individual; // TODO check on integration

  return (
    <div className="InviteStatus">
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      {patientInviteOpened && (
        <PatientInvite
          getInviteMetadata={getInviteMetadata}
          checkPedigreeNameExists={checkPedigreeNameExists}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          getIndividualFamilyData={getIndividualFamilyData}
          updateIndividual={updateEntity}
          getIndividualsColumns={getIndividualsColumns}
          saveAdditionalDatasheetFormatData={saveAdditionalDatasheetFormatData}
          getAdditionalDatasheetFormatData={getAdditionalDatasheetFormatData}
          createPedigree={createPedigree}
          showErrorMessage={showErrorMessage}
          showInfoMessage={showInfoMessage}
          showConfirmMessage={showConfirmMessage}
          getFolders={
            getInviteQueryFolder as (params: {
              context: number;
            }) => Promise<FolderPrimaryListDto[]>
          }
          getFormatFolders={getQueryFormatFolders}
          getQueryFormat={getQueryFormat}
          deleteFormatFolder={deleteQueryFormatFolder}
          saveQueryFormats={saveQueryFormats}
          deleteFormat={deleteQueryFormat}
          createFormatFolder={saveQueryFormatFolder}
          getFormats={(params) =>
            params.folder || !params.searchKeyword?.trim().length
              ? getQueryFormats(params)
              : searchQueryFormats(params)
          }
          getFields={(params) =>
            getFolderFields({
              folderId: params.folderId,
              contextId: individualsContext,
              searchQuery: params.searchQuery,
            })
          }
          getIndividualHistoryLogs={getInviteHistory}
          getPedigrees={getPedigrees}
          getAdminSmartListFunctions={getAdminSmartListFunctions}
          savePedigreesColumns={savePedigreesColumns}
          getPedigreesColumns={getPedigreesColumns}
          getFolderFieldsForAddingPedigreeColumn={getModuleFolderFields}
          getFoldersForAddingPedigreeColumn={getModuleFolders}
          getIndividuals={getIndividuals}
          getPedigreeFolders={getPedigreeFolders}
          getBasicFolders={() => getFolders({ context: individualsContext })}
          getQuestionnaires={() =>
            getQuestionnaires({
              includeFamilyQuestionnaires: true,
              includeFhq4Questionnaires: true,
              includeHiddenQuestionnaires: true
            })
          }
          onClose={closePatientInvite}
          onSubmit={handlePatientInviteSubmit}
          getPedigreeMembersCount={getPedigreeMembersCount}
          getFhqInviteRelatives={getFhqInviteRelatives}
          getFhqInviteIndividuals={getFhqInviteIndividuals}
        />
      )}
      <div className="InviteStatus__global-header">
        <Header
          className="InviteStatus__global-header__description"
          moduleName={I18n.t("Invites")}
        />
        <InvitesFilter
          getInvitesFilter={getInvitesFilter}
          filter={inviteStatusFilter}
          onFilterChange={handleGetFilteredInvites}
          showErrorMessage={showErrorMessage}
        />
        <Flex
          justify="space-between"
          alignItems="center"
          width="100%"
          className="InviteStatus__global-header__controls"
        >
          <Search
            withLiveSearch={invitesStatusConfig.liveSearchEnabled}
            liveSearchDelay={invitesStatusConfig.liveSearchDelay}
            onSearch={handleInvitesSearch}
            value={invitesSearchKeyword}
          />
          <Flex alignItems="center">
            {invitesExportDialogOpened && (
              <InvitesExport
                onSubmit={handleSubmitInvitesExport}
                onClose={closeInvitesExportDialog}
                showErrorMessage={showErrorMessage}
              />
            )}
            {loadQueryManagementType && (
              <QueryManagement
                submitText={
                  loadQueryManagementType === QueryManagementType.edit
                    ? I18n.t("Edit")
                    : undefined
                }
                moduleName={MODULE_NAME}
                withSearch
                onClose={closeLoadQueryManagement}
                showErrorMessage={showErrorMessage}
                showInfoMessage={showInfoMessage}
                deleteFormatFolder={deleteQueryFormatFolder}
                deleteFormat={deleteQueryFormat}
                createFormatFolder={saveQueryFormatFolder}
                getFolders={
                  getInviteQueryFolder as (params: {
                    context: number;
                  }) => Promise<FolderPrimaryListDto[]>
                }
                getFormats={
                  getFormateFromInviteFolder as (params: {
                    searchKeyword?: string;
                    folder?: FolderPrimaryListDto;
                    context?: number;
                  }) => Promise<QueryManagementFormat[]>
                }
                getId={{
                  creatingFolderName: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-name"
                  ).id,
                  creatingFolderItem: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-item"
                  ).id,
                  creatingFolderCollapseItem: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-collapse-item"
                  ).id,
                  creatingFolderCancelBtn: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-cancel-btn"
                  ).id,
                  creatingFolderCreateBtn: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-create-btn"
                  ).id,
                  modalTitle: getTestAttributes(
                    MODULE_NAME,
                    "query-management",
                    "title"
                  ).id,
                  folder: getTestAttributes(
                    MODULE_NAME,
                    "query-management",
                    "folder"
                  ).id,
                  search: getTestAttributes(
                    MODULE_NAME,
                    "query-management",
                    "search"
                  ).id,
                  searchIcon: getTestAttributes(
                    MODULE_NAME,
                    "query-management",
                    "search-icon"
                  ).id,
                }}
                onSubmit={handleQueryManagementSubmit}
              />
            )}
            {queryCreatingDialogOpened && (
              <QueryCreating
                withSearch
                onClose={closeQueryCreatingDialog}
                onSubmit={closeQueryCreatingDialog}
                showErrorMessage={showErrorMessage}
                showConfirmMessage={showConfirmMessage}
                getFolderFields={({ folderId, searchQuery }) =>
                  getFolderFields({
                    folderId,
                    contextId: individualsContext,
                    searchQuery,
                  })
                }
                getFolders={() =>
                  getModuleFolders({
                    folderTypeId: individualsContext,
                    setUp: {
                      rootFolderName: I18n.t("Individual Data Folders"),
                      rootFolderImgUrl: "gif/df_individual.gif",
                    },
                  })
                }
                getId={{
                  creatingFolderName: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-name"
                  ).id,
                  creatingFolderItem: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-item"
                  ).id,
                  creatingFolderCollapseItem: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-collapse-item"
                  ).id,
                  creatingFolderCancelBtn: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-cancel-btn"
                  ).id,
                  creatingFolderCreateBtn: getTestAttributes(
                    MODULE_NAME,
                    "query-management-creating-folder-create-btn"
                  ).id,
                  search: getTestAttributes(
                    MODULE_NAME,
                    "query-creating-search",
                    "input"
                  ).id,
                  searchIcon: getTestAttributes(
                    MODULE_NAME,
                    "query-creating-search",
                    "icon"
                  ).id,
                  modalTitle: "html-invites-query-wnd-header",
                  cancelBtn: "btn-invites-query-wnd-cancel",
                  submitBtn: "btn-invites-query-wnd-apply",
                }}
                moduleName={MODULE_NAME}
              />
            )}
            <span className="InviteStatus__row-count">
              {allInvites.length > 0 ? allInvites.length : invites.length} Row
              {(allInvites.length > 0 ? allInvites.length : invites.length) > 1 ? "s " : " "}
            </span>
            <Button
              disabled={clearQueryBtnDisabled}
              className="InviteStatus__clear-query__btn"
              {...getTestAttributes({ id: "btn-invites-search-query-panel-clear-query" })}
              onClick={handleClearQueryFormatFilter}
            >
              {I18n.t("Clear")}
            </Button>
            <ActionsButton
              openOnClick
              {...getTestAttributes({ id: "inv-search-and-query-panel" })}
              trigger={
                <Button
                  className="InviteStatus__query-dropdown-trigger__btn"
                  {...getTestAttributes({
                    id: "btn-bootstrap-invite-query-dropdown-drop",
                  })}
                >
                  <Flex alignItems="center" justify="space-between">
                    {I18n.t("Query Formats")}
                    <span className="fa fa-caret-down"></span>
                  </Flex>
                </Button>
              }
              menuItems={queryFormatsActions}
            />
            <Button
              {...getTestAttributes({
                id: "btn-invites-search-query-panel-export",
              })}
              className="InviteStatus__export-btn"
              onClick={openInvitesExportDialog}
            >
              {I18n.t("Export")}
            </Button>
            <Button
              {...getTestAttributes({
                id: "btn-invites-search-query-panel-patient",
              })}
              bsStyle="primary"
              onClick={openPatientInvite}
            >
              <i className="fa fa-plus"></i>
              {I18n.t("Invite Patient")}
            </Button>
          </Flex>
        </Flex>
      </div>
      <InvitesTable
        loadItemsCount={INVITES_LOAD_ITEMS_COUNT}
        adminFunctions={adminFunctions}
        filters={filters ? { operators: filters } : undefined}
        createBatchLetters={createBatchLetters}
        validateIndividualEmail={validateIndividualEmail}
        onLaunchNewQuestionnaire={handleLaunchNewQuestionnaire}
        getQuestionnairesForNewQuestionnaire={
          getQuestionnairesForNewQuestionnaire
        }
        getQuestionnaireForInvite={getQuestionnaireForInvite}
        getSentQuestionnaires={getSentQuestionnaires}
        onOpenInviteQuestionnaire={openInviteQuestionnaire}
        getIndividualFamilyData={getIndividualFamilyData}
        getFamilyIdMembersCount={getFamilyIdMembersCount}
        getPedigreeFolders={getPedigreeFolders}
        createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
        checkPedigreeNameExists={checkPedigreeNameExists}
        getQuestionnaires={getQuestionnaires}
        sendInvites={handleNewInvitesSend}
        getIndividualEmail={getIndividualEmail}
        saveIndividualEmail={saveIndividualEmail}
        itemUniqProperty={inviteItemUniqProperty}
        getInviteHistory={getInviteHistory}
        loading={invitesLoading}
        selectedItems={invitesSelectedItems}
        loadMetadata={invitesLoadMetadata}
        totalItemsCount={invitesCount}
        columns={invitesColumns}
        className="InviteStatus__table"
        data={allInvites.length > 0 ? allInvites : invites}
        onRestoreColumnsToDefault={handleRestoreInvitesColumnsToDefault}
        getColumnId={getColumnId}
        openPedigree={openPedigreeFromIndividual}
        openDatasheet={openDatasheetFromIndividual}
        onColumnsResize={updateInvitesColumns}
        showInfoMessage={showInfoMessage}
        closeMessage={closeMessage}
        showMessage={showMessage}
        showErrorMessage={showErrorMessage}
        onColumnsOrderChange={handleInvitesColumnsOrderChange}
        onSelectedItemsChange={handleInviteSelectedItemsChange}
        getFields={getFieldsForInvitesColumn}
        getFoldersForAddingColumns={getFoldersForInvitesColumn}
        generateLetter={generateLetterFromInvites}
        updateInvitesLockStatus={handleUpdateInvitesLockStatus}
        getReviewedStatusState={getReviewedStatusState}
        updateReviewedStatusActions={handleUpdateReviewedStatusActions}
        updateInviteStatus={handleUpdateInviteStatus}
        getData={(loadMetadata) => {
          return handleGetInvites({
            columns: invitesColumns,
            loadedInvites: allInvites.length > 0 ? allInvites : invites,
            searchKeyword: invitesSearchKeyword,
            ...loadMetadata,
            ...inviteStatusFilter,
          });
        }}
      />
    </div>
  );
};
